import { queryClient } from '../context/Api.context';
import { Environment } from '../env/Environment';
import { LARGE_ORDER_THRESHOLD } from '../utils/constants';
import { Cornerstone } from '@rpi/api';
import { Trans } from '@rpi/i18n';
import React from 'react';

export type RemovedItem = {
  itemIndex: number;
  itemId: number;
  projectId: Cornerstone.ProjectResult['id'];
  sku: string;
  quantity: number;
  title: Cornerstone.ProjectResult['title'];
};

const getNextDiscount = (
  volumeDiscounts: Cornerstone.Discount[] | undefined,
) => {
  if (!volumeDiscounts) {
    return;
  }
  const nextDiscount = volumeDiscounts.sort((a, b) => {
    const minA = a.cartQualifications?.[0]?.minUnits ?? 0;
    const minB = b.cartQualifications?.[0]?.minUnits ?? 0;
    return minA - minB;
  });

  return nextDiscount[0];
};

const getDiscountValues = (discount: Cornerstone.Discount | undefined) => {
  if (!discount) {
    return;
  }

  const cartQualifications = discount.cartQualifications;
  if (!cartQualifications || cartQualifications.length === 0) {
    return;
  }

  const qualification = cartQualifications[0];

  return {
    minUnits: qualification.minUnits,
    maxUnits: qualification.maxUnits,
    discountPercentage: (discount.discountPercentage ?? 0) * 100,
  };
};

export const getMaxQuantityProductClass = (
  lineItems: Cornerstone.CheckoutLineItem[] | undefined,
) => {
  if (!lineItems || lineItems.length === 0) {
    return;
  }

  const productClassMap = new Map<string, number>();
  lineItems.forEach((item) => {
    const productClass = item.sku?.split('-')[0];
    const currentQuantity = productClassMap.get(productClass) || 0;
    productClassMap.set(productClass, currentQuantity + item.quantity);
  });

  const productClassQuantities = Array.from(productClassMap.entries()).map(
    ([productClass, quantity]) => ({
      productClass,
      quantity,
    }),
  );

  return productClassQuantities.reduce((maxItem, currItem) => {
    return currItem.quantity > maxItem.quantity ? currItem : maxItem;
  });
};

const findCurrentVolumeDiscount = (
  volumeDiscounts: Cornerstone.Discount[] | undefined,
  quantity: number | undefined,
  productClass: string | undefined,
) => {
  if (
    !volumeDiscounts ||
    volumeDiscounts.length === 0 ||
    !quantity ||
    !productClass
  ) {
    return;
  }
  return volumeDiscounts.find((discount) => {
    if (discount.cartQualifications && discount.cartQualifications.length > 0) {
      const qualification = discount.cartQualifications[0];
      const minUnits = qualification.minUnits ?? 0;
      const maxUnits = qualification.maxUnits ?? quantity;
      return qualification.requiredSkus?.includes(productClass) &&
        quantity >= minUnits &&
        quantity <= maxUnits
        ? discount
        : null;
    }
    return null;
  });
};

const findAdditionalVolumeDiscounts = (
  volumeDiscounts: Cornerstone.Discount[] | undefined,
  quantity: number | undefined,
  productClass: string | undefined,
) => {
  if (
    !volumeDiscounts ||
    volumeDiscounts.length === 0 ||
    !quantity ||
    !productClass
  ) {
    return;
  }

  return volumeDiscounts.filter((discount) => {
    if (discount.cartQualifications && discount.cartQualifications.length > 0) {
      const qualification = discount.cartQualifications[0];
      const minUnits = qualification.minUnits ?? 0;
      return qualification.requiredSkus?.includes(productClass) &&
        quantity < minUnits
        ? discount
        : null;
    }
    return null;
  });
};

type DiscountValues = {
  minUnits: number | undefined;
  maxUnits: number | undefined;
  discountPercentage: number | undefined;
};

const getCurrentAndNextVolumeDiscountText = (
  currentVolumeDiscount: Cornerstone.Discount | undefined,
  nextVolumeDiscountValues: DiscountValues | undefined,
) => {
  if (!currentVolumeDiscount) {
    return;
  }
  const currentDiscountPercentage =
    (currentVolumeDiscount.discountPercentage ?? 0) * 100;

  if (nextVolumeDiscountValues) {
    const nextDiscountMinUnits = nextVolumeDiscountValues.minUnits ?? 0;
    const nextDiscountMaxUnits = nextVolumeDiscountValues.maxUnits;
    const nextRange = nextDiscountMaxUnits
      ? `${nextDiscountMinUnits}-${nextDiscountMaxUnits}`
      : `${nextDiscountMinUnits}`;
    const nextDiscountPercentage = nextVolumeDiscountValues.discountPercentage;

    return nextDiscountMaxUnits
      ? {
          i18nKey: 'route.cart.next_discount_range',
          currentDiscountPercentage,
          nextDiscountPercentage,
          nextDiscountRange: nextRange,
        }
      : {
          i18nKey: 'route.cart.next_discount_or_more',
          currentDiscountPercentage,
          nextDiscountPercentage,
          nextDiscountRange: nextRange,
        };
  }
  return {
    i18nKey: 'route.cart.qualifies_for_discount',
    currentDiscountPercentage,
  };
};

const getNextVolumeDiscountText = (
  nextVolumeDiscountValues: DiscountValues | undefined,
) => {
  if (nextVolumeDiscountValues) {
    const nextDiscountMinUnits = nextVolumeDiscountValues.minUnits ?? 0;
    const nextDiscountPercentage = nextVolumeDiscountValues.discountPercentage;

    return {
      i18nKey: 'route.cart.next_discount',
      nextDiscountPercentage,
      nextDiscountMinUnits,
    };
  }
};

type VolumeDiscountValues = {
  i18nKey: string;
  currentDiscountPercentage?: number;
  nextDiscountPercentage?: number;
  nextDiscountRange?: string;
  nextDiscountMinUnits?: number;
};

type QuantityProductClass = {
  productClass: string;
  quantity: number;
};

export const volumeDiscountValues = (
  volumeDiscounts: Cornerstone.Discount[] | undefined,
  maxQuantityProductClass: QuantityProductClass | undefined,
): VolumeDiscountValues | undefined => {
  // how this works:
  // find the max quantity by product class
  // see if there are volume discounts for that product class
  // if product class does not qualify for discount and a volume discount is available, show them discount for the lowest min quantity
  // if the product class qualifies for discount and volume discount available, say hooray and tell them the next largest discount
  // if the product class is 100 and above, talk about large order services

  if (
    !volumeDiscounts ||
    volumeDiscounts.length === 0 ||
    !maxQuantityProductClass
  ) {
    return;
  }

  if (maxQuantityProductClass) {
    const quantity = maxQuantityProductClass.quantity;
    const productClass = maxQuantityProductClass.productClass;

    // if the product class quantity >= 100, show LOS message
    if (quantity > LARGE_ORDER_THRESHOLD) {
      return {
        i18nKey: 'route.cart.large_orders',
      };
    } else {
      // check if there is a current volume discount for that product class
      const currentVolumeDiscount = findCurrentVolumeDiscount(
        volumeDiscounts,
        quantity,
        productClass,
      );

      // check if there are any volume discounts for larger quantities of that product class
      const nextVolumeDiscounts = findAdditionalVolumeDiscounts(
        volumeDiscounts,
        quantity,
        productClass,
      );
      const nextVolumeDiscount = getNextDiscount(nextVolumeDiscounts);
      const nextVolumeDiscountValues = getDiscountValues(nextVolumeDiscount);

      // if the product class qualifies for discount and another volume discount available, say hooray and tell them the next discount threshold
      if (currentVolumeDiscount) {
        return getCurrentAndNextVolumeDiscountText(
          currentVolumeDiscount,
          nextVolumeDiscountValues,
        );
      }

      // if product class does not qualify for discount and a volume discount is available, show them quantity threshold to get the discount
      return getNextVolumeDiscountText(nextVolumeDiscountValues);
    }
  }
};

export const getVolumeDiscountByUnit = (
  volumeDiscounts: Cornerstone.Discount[] | undefined,
  maxQuantityProductClass: QuantityProductClass | undefined,
) => {
  if (
    !volumeDiscounts ||
    volumeDiscounts.length === 0 ||
    !maxQuantityProductClass
  ) {
    return;
  }
  let sortedVolumeDiscounts: Cornerstone.Discount[] | undefined;
  if (maxQuantityProductClass) {
    const filteredVolumeDiscounts = volumeDiscounts.filter((discount) =>
      discount.discountedSkus?.includes(maxQuantityProductClass.productClass),
    );
    if (filteredVolumeDiscounts && filteredVolumeDiscounts.length > 0) {
      sortedVolumeDiscounts = filteredVolumeDiscounts.sort((a, b) => {
        const aCartQualificationsMin = a.cartQualifications?.[0]?.minUnits ?? 0;
        const bCartQualificationsMin = b.cartQualifications?.[0]?.minUnits ?? 0;

        return aCartQualificationsMin - bCartQualificationsMin;
      });
    }
  }

  return sortedVolumeDiscounts?.map((discount) => {
    const cartQualifications = discount.cartQualifications?.[0];
    return {
      minUnits: cartQualifications?.minUnits,
      maxUnits: cartQualifications?.maxUnits,
      discountPercentage: discount.discountPercentage,
    };
  });
};

const useRemovedItems = (handleInvalidate: () => void) => {
  const [removedItems, setRemovedItems] = React.useState<RemovedItem[]>([]);

  const addItemMutation = Cornerstone.useAddItemV2({
    mutation: {
      onSuccess: () => {
        handleInvalidate();
      },
    },
  });

  const removeItem = (item: RemovedItem) => {
    setRemovedItems((prev) => [...prev, item]);
  };

  const dismissRemovedItem = (item: RemovedItem) => {
    setRemovedItems((prev) =>
      prev.filter((removedItem) => removedItem.itemId !== item.itemId),
    );
  };

  const readdItem = (item: RemovedItem) => {
    addItemMutation.mutate(
      { data: item },
      { onSuccess: () => dismissRemovedItem(item) },
    );
  };

  const clearRemovedItems = () => {
    setRemovedItems([]);
  };

  return {
    removedItems,
    removeItem,
    readdItem,
    dismissRemovedItem,
    clearRemovedItems,
  };
};

export const useCartHook = () => {
  const handleInvalidate = () => {
    queryClient.invalidateQueries(['checkoutSummary']);
  };

  const {
    removedItems,
    removeItem,
    dismissRemovedItem,
    clearRemovedItems,
    readdItem,
  } = useRemovedItems(handleInvalidate);

  const { data } = Cornerstone.useGetCartV2({
    query: {
      queryKey: ['checkoutSummary'],
    },
  });

  const updateQuantityMutation = Cornerstone.useUpdateQuantityV2({
    mutation: {
      onSuccess: () => {
        handleInvalidate();
      },
    },
  });

  const updateItemQuantity = React.useCallback(
    (id: number, newQuantity: number) => {
      const lineItemForQuantityUpdate: Cornerstone.CartItemUpdateRequest = {
        lineItemId: id,
        quantity: newQuantity,
      };
      updateQuantityMutation.mutate({ data: lineItemForQuantityUpdate });
    },
    [updateQuantityMutation],
  );

  const removeItemMutation = Cornerstone.useRemoveItemV2({
    mutation: {
      onSuccess: () => {
        handleInvalidate();
      },
      onError: (error: any) => {
        console.error({
          title: 'Failed to price cart for line items',
          description: error?.response?.data?.message || error.message,
          type: 'error',
        });
      },
    },
  });

  const removeFromCart = React.useCallback(
    (itemToRemove: RemovedItem) => {
      const cartItemDeleteRequest: Cornerstone.CartItemDeleteRequest = {
        lineItemId: itemToRemove.itemId,
      };
      removeItemMutation.mutate(
        { data: cartItemDeleteRequest },
        {
          onSuccess: () => {
            removeItem(itemToRemove);
          },
        },
      );
    },
    [removeItemMutation, removeItem],
  );

  const clearCartMutation = Cornerstone.useClearCart({
    mutation: {
      onSuccess: () => {
        clearRemovedItems();
        handleInvalidate();
      },
      onError: (error: any) => {
        console.error({
          title: 'Failed to clear cart',
          description: error?.response?.data?.message || error.message,
          type: 'error',
        });
      },
    },
  });

  const clearCart = React.useCallback(() => {
    clearCartMutation.mutate();
  }, [clearCartMutation]);

  const { data: volumeDiscounts } = Cornerstone.useGetVolumeDiscounts({
    query: {
      queryKey: ['volumeDiscounts'],
    },
  });

  const maxQuantityProductClass = React.useMemo(() => {
    return getMaxQuantityProductClass(data?.data.lineItems);
  }, [data?.data.lineItems]);

  const bannerValues = React.useMemo(() => {
    return volumeDiscountValues(volumeDiscounts?.data, maxQuantityProductClass);
  }, [maxQuantityProductClass, volumeDiscounts?.data]);

  const bannerText = React.useMemo(() => {
    if (!bannerValues) {
      return;
    }
    switch (bannerValues.i18nKey) {
      case 'route.cart.large_orders':
        return (
          <Trans
            i18nKey={bannerValues.i18nKey}
            components={{
              'los-link': (
                <a
                  aria-label="LOS Link"
                  className="text-white"
                  href={Environment.LINK.BLURB_LOS}
                  target="_blank"
                  rel="noopener noreferrer"
                />
              ),
            }}
          />
        );
      case 'route.cart.next_discount_range':
        return (
          <Trans
            i18nKey={bannerValues.i18nKey}
            values={{
              'discount-percentage': bannerValues.currentDiscountPercentage,
              'next-discount': bannerValues.nextDiscountPercentage,
              'next-discount-range': bannerValues.nextDiscountRange,
            }}
          />
        );
      case 'route.cart.next_discount_or_more':
        return (
          <Trans
            i18nKey={bannerValues.i18nKey}
            values={{
              'discount-percentage': bannerValues.currentDiscountPercentage,
              'next-discount': bannerValues.nextDiscountPercentage,
              'next-discount-range': bannerValues.nextDiscountRange,
            }}
          />
        );
      case 'route.cart.qualifies_for_discount':
        return (
          <Trans
            i18nKey={bannerValues.i18nKey}
            values={{
              'discount-percentage': bannerValues.currentDiscountPercentage,
            }}
          />
        );

      case 'route.cart.next_discount':
        return (
          <Trans
            i18nKey={bannerValues.i18nKey}
            values={{
              'next-discount': bannerValues.nextDiscountPercentage,
              'next-discount-min': bannerValues.nextDiscountMinUnits,
            }}
          />
        );
    }
  }, [bannerValues]);

  const volumeDiscountsByUnit = React.useMemo(() => {
    return getVolumeDiscountByUnit(
      volumeDiscounts?.data,
      maxQuantityProductClass,
    );
  }, [maxQuantityProductClass, volumeDiscounts?.data]);

  return {
    cart: data?.data,
    removedItems,
    dismissRemovedItem,
    readdItem,
    updateItemQuantity,
    removeFromCart,
    clearCart,
    handleInvalidate,
    deleteLoading: removeItemMutation.isLoading,
    updateLoading: updateQuantityMutation.isLoading,
    clearLoading: clearCartMutation.isLoading,
    bannerText,
    maxQuantityProductClass,
    volumeDiscountsByUnit,
  };
};
